import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/ProfileTable.jsx
import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    Button,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    Assignment as AssignIcon,
    ContentCopy as DuplicateIcon,
    MoreVert as MoreIcon,
    Visibility as VisibilityIcon,
    Star as StarIcon
} from '@mui/icons-material';

const ProfileTable = ({
    profiles,
    onEdit,
    onAssign,
    onDuplicate,
    onMenuOpen,
    onViewDetail,
    hasPermission,
    getProfileAssignments
}) => {
  const { t } = useTranslation();
    const formatThreshold = (config) => {
        if (!config) return '—';

        const min = config.threshold_min;
        const max = config.threshold_max;
        const ideal = config.ideal_value;

        let range = '';
        if (min != null && max != null) range = `${min}–${max}`;
        else if (min != null) range = `≥${min}`;
        else if (max != null) range = `≤${max}`;
        else range = '—';

        if (ideal != null) {
            return `${range} (⭐${ideal})`;
        }

        return range;
    };

    const getConfigsByAxle = (profile) => {
        const configsByAxle = {};

        if (profile.applies_to_all_axles) {
            const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === 1);
            const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === 1);

            configsByAxle['Todos'] = {
                psi: psiConfig,
                temp: tempConfig
            };
        } else {
            for (let i = 1; i <= profile.axle_count; i++) {
                const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === i);
                const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === i);

                configsByAxle[t('hardcoded.components_alarmprofiles_profilecard.eje_i', { i: i })] = {
                    psi: psiConfig,
                    temp: tempConfig
                };
            }
        }

        return configsByAxle;
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                                Nombre
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="subtitle2" fontWeight={600}>
                                {i18n.t('navbar.settings')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {i18n.t('gps.stats.assigned')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {i18n.t('gps.table.status')}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {t('common.actions')}</Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {profiles.map((profile) => {
                        const profileAssignments = getProfileAssignments(profile.id);
                        const configsByAxle = getConfigsByAxle(profile);

                        return (
                            <TableRow
                                key={profile.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => onViewDetail(profile)}
                            >
                                <TableCell>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        {profile.name}
                                    </Typography>
                                    {profile.description && (
                                        <Typography variant="caption" color="text.secondary">
                                            {profile.description}
                                        </Typography>
                                    )}
                                </TableCell>

                                <TableCell>
                                    <Typography variant="body2" fontWeight={600} gutterBottom>
                                        {profile.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.ejes')}{profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_profilecard.por_eje')}
                                    </Typography>

                                    <Table size="small" sx={{ '& td': { py: 0.25, fontSize: '0.7rem', border: 'none' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', border: 'none' }}>
                                                    {i18n.t('hardcoded.components_alarmprofiles_profiledetail.ejes')}</TableCell>
                                                <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', border: 'none' }}>
                                                    {t('hardcoded.components_alarmprofiles_profilecard.presion')}</TableCell>
                                                <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', border: 'none' }}>
                                                    {i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</TableCell>
                                                <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary', border: 'none' }}>
                                                    {t('hardcoded.components_alarmprofiles_profiledetail.optimo')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(configsByAxle).map(([axleName, configs]) => (
                                                <TableRow key={axleName}>
                                                    <TableCell>{axleName}</TableCell>
                                                    <TableCell>
                                                        {formatThreshold(configs.psi)}
                                                        {configs.psi && ` ${configs.psi.unit}`}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatThreshold(configs.temp)}
                                                        {configs.temp && `° ${configs.temp.unit}`}
                                                    </TableCell>
                                                    <TableCell>
                                                        {[
                                                            configs.psi?.ideal_value != null ? `P: ${configs.psi.ideal_value} ${configs.psi.unit}` : null,
                                                            configs.temp?.ideal_value != null ? `T: ${configs.temp.ideal_value} ${configs.temp.unit}` : null
                                                        ].filter(Boolean).join(' | ')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableCell>

                                <TableCell align="center">
                                    <Typography variant="body2" fontWeight={700}>
                                        {profileAssignments.length}
                                    </Typography>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                        {profile.is_template && (
                                            <Chip label={t('hardcoded.components_alarmprofiles_profilecard.plantilla')} size="small" color="secondary" sx={{ fontSize: '0.7rem', height: 20 }} />
                                        )}
                                        <Chip
                                            label={profile.active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                                            size="small"
                                            color={profile.active ? 'success' : 'default'}
                                            variant={profile.active ? 'filled' : 'outlined'}
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    </Box>
                                </TableCell>

                                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        {profile.is_template ? (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DuplicateIcon />}
                                                onClick={() => onDuplicate(profile)}
                                                disabled={!hasPermission('manager')}
                                            >
                                                {i18n.t('hardcoded.components_alarmprofiles_profilecard.usar')}</Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                startIcon={<AssignIcon />}
                                                onClick={() => onAssign(profile)}
                                                disabled={!hasPermission('manager') || !profile.active}
                                            >
                                                {i18n.t('common.assign')}</Button>
                                        )}

                                        <Tooltip title={t('hardcoded.components_alarmprofiles_profilecard.ver_detalle')}>
                                            <IconButton size="small" onClick={() => onViewDetail(profile)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        {hasPermission('manager') && (
                                            <IconButton size="small" onClick={(e) => onMenuOpen(e, profile)}>
                                                <MoreIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProfileTable;